<script setup lang="ts" generic="TParams extends any[]">
import type {
    Ability,
    AsyncAbility,
} from '@precon-tools-suite-by-shared/shared-auth-js-ability-checks';
import { useAbilityWithParams } from '../composables/useAbility';
import Skeleton from 'primevue/skeleton';

const props = withDefaults(
    defineProps<{
        /**
         * The ability used in this gate.
         */
        ability: Ability<TParams> | AsyncAbility<TParams>;
        /**
         * The ability parameters.
         */
        parameters: TParams;
        /**
         * Do not render anything if the gate is loading. (only applicable for async abilities.)
         */
        hideLoading?: boolean;
        /**
         * Do not render anything if the gate is locked.
         */
        hideLocked?: boolean;
        /**
         * The severity level of the locked message.
         */
        lockedSeverity?: string;
        /**
         * The locked message.
         */
        lockedMessage?: string;
        /**
         * The icon for the locked message.
         */
        lockedIcon?: string;
    }>(),
    {
        lockedSeverity: 'warn',
        lockedMessage: 'Geen toestemming',
        lockedIcon: 'pi-lock',
    }
);

const { allowed, loading, missing } = useAbilityWithParams(
    props.ability,
    () => props.parameters
);
</script>

<template>
    <template v-if="allowed">
        <slot />
    </template>

    <template v-else-if="loading">
        <slot name="loading">
            <Skeleton v-bind="$attrs" />
        </slot>
    </template>

    <template v-else-if="!hideLocked">
        <slot name="locked" :missing="missing">
            <div
                class="p-message p-component"
                :class="`p-message-${lockedSeverity}`"
                role="alert"
                v-bind="$attrs"
            >
                <div class="p-message-wrapper">
                    <span class="p-message-icon pi" :class="lockedIcon" />
                    <div class="p-message-text">
                        <slot name="message">
                            {{ lockedMessage }}
                        </slot>
                    </div>
                </div>
            </div>
        </slot>
    </template>
</template>
